import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { FormField, fieldA11y } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/ui/input';
import { isChosen, type SourceChoice } from './sourceModel';
import type { ScmValues } from './scmConnectionModel';
import type { DiscoverScmToken } from './useDiscoverScmToken';

function Note({ icon, tone, children, testId }: { icon: string; tone: string; children: ReactNode; testId: string }) {
  return (
    <p data-testid={testId} className="flex items-start gap-1.5 text-xs text-muted-foreground">
      <Icon name={icon} size={15} className={cn('mt-px shrink-0', tone)} />
      <span className="min-w-0">{children}</span>
    </p>
  );
}

function ChoiceList({ d, values }: { d: DiscoverScmToken; values: ScmValues }) {
  return (
    <div role="radiogroup" aria-label="What the token reaches" className="flex flex-col gap-1.5" data-testid="scm-source-choices">
      {d.choices.map((c: SourceChoice) => {
        const chosen = isChosen(c, values);
        return (
          <button key={c.key} type="button" role="radio" aria-checked={chosen} data-testid={`scm-source-${c.key}`} onClick={() => d.pick(c)}
            className={cn('flex min-h-11 items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors duration-150', chosen ? 'border-primary/40 bg-primary/5' : 'bg-card hover:bg-secondary')}>
            <Icon name={chosen ? 'radio_button_checked' : 'radio_button_unchecked'} size={18} className={chosen ? 'text-primary' : 'text-subtle'} />
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-semibold text-foreground">{c.label}</span>
              <span className="block truncate font-mono text-[11px] text-subtle">{c.detail}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function UrlField({ d, values, error }: { d: DiscoverScmToken; values: ScmValues; error: string | undefined }) {
  const what = values.kind === 'repository' ? 'repository' : 'workspace';
  const shown = error ?? (d.url.trim() && !d.parsed ? 'Not a Bitbucket URL: copy it from the address bar while the ' + what + ' is open' : undefined);
  const hint = d.parsed ? `Workspace ${d.parsed.workspace}${d.parsed.repository && values.kind === 'repository' ? ` · repository ${d.parsed.repository}` : ''}` : `Open the ${what} in Bitbucket and copy the address bar: bitbucket.org/<workspace>${values.kind === 'repository' ? '/<repository>' : ''}`;
  return (
    <FormField id="scm-connection-url" label={`${what === 'repository' ? 'Repository' : 'Workspace'} URL`} required hint={hint} error={shown}>
      <Input data-testid="scm-connection-url" value={d.url} onChange={(e) => d.onUrl(e.target.value)} placeholder={`https://bitbucket.org/acme${values.kind === 'repository' ? '/acme-shop-mobile' : '/'}`} className="font-mono sm:text-[13px]" spellCheck={false} autoComplete="off" {...fieldA11y('scm-connection-url', shown, 'hint')} />
    </FormField>
  );
}

/** What the pasted token reaches: Bitbucket's answer, the choice (the only one is picked), or the URL when Bitbucket does not say. */
export function TokenSource({ d, values, error, stored }: { d: DiscoverScmToken; values: ScmValues; error: string | undefined; stored: boolean }) {
  if (stored || (!d.searching && !d.result)) {
    if (values.workspace) return <Note testId="scm-source-summary" icon="link" tone="text-succeeded">Connects <span className="font-mono">bitbucket.org/{values.workspace}{values.repository ? `/${values.repository}` : ''}</span></Note>;
    return <Note testId="scm-source-waiting" icon="info" tone="text-subtle">Paste the token: Hangar asks Bitbucket what it reaches, so you do not type the workspace or repository.{error ? <span className="block text-failed">{error}</span> : null}</Note>;
  }
  if (d.searching) return <Note testId="scm-source-searching" icon="progress_activity" tone="animate-spin text-subtle motion-reduce:animate-none">Asking Bitbucket what this token reaches…</Note>;
  if (d.result?.outcome === 'rejected') return <Note testId="scm-source-rejected" icon="error" tone="text-failed">{d.result.message ?? 'Bitbucket rejected the token'}: copy it again from Bitbucket, or create a new one.</Note>;
  return (
    <div className="flex flex-col gap-2">
      {d.choices.length > 0 ? <ChoiceList d={d} values={values} /> : <Note testId="scm-source-unsupported" icon="info" tone="text-subtle">{values.kind === 'repository' ? 'Bitbucket does not tell Hangar which repository a repository access token belongs to: paste the repository URL from your browser.' : 'Bitbucket did not say which workspace this token reaches: paste the workspace URL from your browser.'}</Note>}
      {d.needsUrl ? <UrlField d={d} values={values} error={error} /> : error ? <p className="text-xs text-failed">{error}</p> : null}
    </div>
  );
}
